import { z } from 'zod';
import type {
  CombatResult,
  CompiledArtifact,
  EfficiencyGrade,
  FeatureId,
  LessonCheckReport,
  ModuleCategory,
  ModuleKind,
  PipelineReport,
  ResourceProfile,
  SlotId,
  WorldFixture,
} from '@codeframe/game';
import type { HintLevel, HintSet } from '@codeframe/lang-core';

/**
 * HTTP API contract shared by server and web client. Request bodies are zod schemas (the
 * server validates every request with them); responses are TypeScript types.
 * All endpoints live under /api. Errors: { error: { code, message, details? } }.
 */

// ---------------------------------------------------------------------------------------
// Common
// ---------------------------------------------------------------------------------------

export interface ApiErrorBody {
  readonly error: { readonly code: string; readonly message: string; readonly details?: unknown };
}

export const HintLevelSchema = z.enum(['beginner', 'normal', 'advanced', 'expert']);
export const LanguageSchema = z.enum(['python']);
const sourceCode = z.string().max(20_000);

// ---------------------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------------------

export const RegisterRequestSchema = z.object({
  username: z
    .string()
    .trim()
    .min(3, 'Callsign must be at least 3 characters')
    .max(24, 'Callsign must be at most 24 characters')
    .regex(/^[A-Za-z0-9_-]+$/, 'Use letters, digits, - and _ only'),
  email: z.string().trim().toLowerCase().email('Enter a valid email address').max(254),
  password: z.string().min(10, 'Password must be at least 10 characters').max(200),
});
export type RegisterRequest = z.infer<typeof RegisterRequestSchema>;

export const LoginRequestSchema = z.object({
  login: z.string().trim().min(1).max(254),
  password: z.string().min(1).max(200),
});
export type LoginRequest = z.infer<typeof LoginRequestSchema>;

export interface SessionUserDto {
  readonly id: string;
  readonly username: string;
  readonly email: string;
}

export interface MeResponse {
  readonly user: SessionUserDto;
  readonly player: PlayerProfileDto;
}

// ---------------------------------------------------------------------------------------
// Player
// ---------------------------------------------------------------------------------------

export interface LevelDto {
  readonly level: number;
  readonly xp: number;
  readonly levelStartXp: number;
  readonly nextLevelXp: number | null;
  readonly progress: number;
}

export interface FeatureDto {
  readonly id: FeatureId;
  readonly name: string;
  readonly description: string;
  readonly minLevel: number;
  readonly implemented: boolean;
  readonly unlocked: boolean;
}

export interface PlayerProfileDto {
  readonly id: string;
  readonly displayName: string;
  readonly level: LevelDto;
  readonly programming: LevelDto;
  readonly languages: readonly { readonly language: string; readonly displayName: string; readonly xp: number; readonly percent: number }[];
  readonly disciplines: readonly { readonly id: string; readonly name: string; readonly description: string; readonly xp: number }[];
  readonly concepts: readonly { readonly id: string; readonly name: string; readonly xp: number }[];
  readonly features: readonly FeatureDto[];
  readonly settings: { readonly hintLevel: HintLevel; readonly preferredLanguage: string };
  readonly stats: {
    readonly lessonsCompleted: number;
    readonly modulesCompiled: number;
    readonly battlesWon: number;
    readonly battlesFought: number;
  };
  readonly createdAt: string;
}

export const UpdateSettingsRequestSchema = z
  .object({ hintLevel: HintLevelSchema.optional(), preferredLanguage: LanguageSchema.optional() })
  .refine((v) => Object.keys(v).length > 0, 'Nothing to update');
export type UpdateSettingsRequest = z.infer<typeof UpdateSettingsRequestSchema>;

/** Rewards actually granted by an action (lesson, contract, battle). */
export interface RewardPartDto {
  readonly id: string;
  readonly name: string;
  readonly category: string;
}

export interface RewardGrantDto {
  readonly playerXp: number;
  readonly programmingXp: number;
  readonly languageXp: number;
  readonly frameXp: number;
  readonly disciplineXp: Readonly<Record<string, number>>;
  readonly unlocks: readonly string[];
  /** Hardware granted by this reward, ready to install in the Frame bay. */
  readonly parts: readonly RewardPartDto[];
  readonly levelBefore: number;
  readonly levelAfter: number;
  readonly newFeatures: readonly FeatureDto[];
}

// ---------------------------------------------------------------------------------------
// Academy
// ---------------------------------------------------------------------------------------

export type LessonStatus = 'locked' | 'available' | 'in_progress' | 'completed';

export interface LessonSummaryDto {
  readonly id: string;
  readonly title: string;
  readonly summary: string;
  readonly concept: string;
  readonly order: number;
  readonly estimatedMinutes: number;
  readonly status: LessonStatus;
  readonly stepsCompleted: number;
  readonly stepsTotal: number;
  readonly rewards: { readonly playerXp: number; readonly parts: readonly RewardPartDto[] };
  readonly lockedReason?: string;
}

export interface CourseDto {
  readonly id: string;
  readonly language: string;
  readonly title: string;
  readonly description: string;
  readonly lessons: readonly LessonSummaryDto[];
}

export interface PublicTestDto {
  readonly id: string;
  readonly name: string;
  readonly scenario?: string;
}

interface StepBaseDto {
  readonly id: string;
  readonly title: string;
  readonly body: string;
  readonly completed: boolean;
}

export type StepDto =
  | (StepBaseDto & { readonly kind: 'explain' })
  | (StepBaseDto & { readonly kind: 'demonstrate'; readonly code: string; readonly runWorld?: WorldFixture })
  | (StepBaseDto & {
      readonly kind: 'guided' | 'debug' | 'challenge' | 'apply';
      readonly starterCode: string;
      readonly savedCode?: string;
      readonly hints: readonly HintSet[];
      readonly visibleTests: readonly PublicTestDto[];
      readonly hiddenTestCount: number;
      readonly runWorld?: WorldFixture;
      readonly usesGameApi: boolean;
    })
  | (StepBaseDto & { readonly kind: 'predict'; readonly code: string; readonly choices: readonly string[] });

export interface LessonDto {
  readonly id: string;
  readonly courseId: string;
  readonly language: string;
  readonly title: string;
  readonly summary: string;
  readonly concept: string;
  readonly status: LessonStatus;
  readonly steps: readonly StepDto[];
  readonly nextLessonId: string | null;
  readonly rewards: { readonly playerXp: number; readonly programmingXp: number };
}

export const CheckStepRequestSchema = z.object({ code: sourceCode });
export type CheckStepRequest = z.infer<typeof CheckStepRequestSchema>;

export interface CheckStepResponse {
  readonly report: LessonCheckReport;
  readonly stepCompleted: boolean;
  readonly lessonCompleted: boolean;
  readonly rewards: RewardGrantDto | null;
}

export const AnswerStepRequestSchema = z.object({ choice: z.number().int().min(0).max(20) });
export type AnswerStepRequest = z.infer<typeof AnswerStepRequestSchema>;

export interface AnswerStepResponse {
  readonly correct: boolean;
  readonly explanation: string;
  readonly correctChoice: number | null;
  readonly stepCompleted: boolean;
  readonly lessonCompleted: boolean;
  readonly rewards: RewardGrantDto | null;
}

export interface CompleteStepResponse {
  readonly stepCompleted: boolean;
  readonly lessonCompleted: boolean;
  readonly rewards: RewardGrantDto | null;
}

// ---------------------------------------------------------------------------------------
// Workshop & Code Library
// ---------------------------------------------------------------------------------------

export const ModuleCategoryRequestSchema = z.enum(['combat', 'defence', 'cyber', 'utility']);
export const ModuleKindRequestSchema = z.enum(['passive_routine', 'trigger', 'active_ability', 'targeting', 'movement', 'firewall', 'breach']);

export const ValidateRequestSchema = z.object({
  source: sourceCode,
  language: LanguageSchema,
  moduleKind: ModuleKindRequestSchema.default('passive_routine'),
  contractId: z.string().max(100).optional(),
});
export type ValidateRequest = z.input<typeof ValidateRequestSchema>;

/** A pipeline report as sent to clients: the compiled IR is never included. */
export type PipelineReportDto = Omit<PipelineReport, 'artifact'> & {
  readonly artifact?: Omit<CompiledArtifact, 'program'>;
};

export interface ContractDto {
  readonly id: string;
  readonly title: string;
  readonly brief: string;
  readonly objective: string;
  readonly category: ModuleCategory;
  readonly moduleKind: ModuleKind;
  readonly suggestedName: string;
  readonly starterCode: string;
  readonly visibleTests: readonly PublicTestDto[];
  readonly hiddenTestCount: number;
  readonly rewards: { readonly playerXp: number; readonly parts: readonly RewardPartDto[] };
  readonly status: 'locked' | 'available' | 'completed';
  readonly lockedReason?: string;
}

const moduleName = z
  .string()
  .trim()
  .min(2, 'Name must be at least 2 characters')
  .max(40, 'Name must be at most 40 characters')
  .regex(/^[A-Za-z0-9][A-Za-z0-9 _\-.'/]*$/, 'Use letters, digits, spaces and - _ . \' /');

export const CreateModuleRequestSchema = z.object({
  name: moduleName,
  category: ModuleCategoryRequestSchema,
  source: sourceCode,
  language: LanguageSchema,
  moduleKind: ModuleKindRequestSchema.default('passive_routine'),
  contractId: z.string().max(100).optional(),
});
export type CreateModuleRequest = z.input<typeof CreateModuleRequestSchema>;

export const NewVersionRequestSchema = z.object({ source: sourceCode, contractId: z.string().max(100).optional() });
export type NewVersionRequest = z.infer<typeof NewVersionRequestSchema>;

export const UpdateModuleRequestSchema = z
  .object({ name: moduleName.optional(), category: ModuleCategoryRequestSchema.optional() })
  .refine((v) => Object.keys(v).length > 0, 'Nothing to update');
export type UpdateModuleRequest = z.infer<typeof UpdateModuleRequestSchema>;

export interface ModuleVersionDto {
  readonly id: string;
  readonly version: number;
  readonly createdAt: string;
  readonly sourceHash: string;
  readonly resources: ResourceProfile;
  readonly apiUsage: readonly string[];
  readonly capabilities: readonly string[];
  readonly concepts: readonly string[];
}

export interface ModuleDto {
  readonly id: string;
  readonly name: string;
  readonly category: ModuleCategory;
  readonly kind: ModuleKind;
  readonly language: string;
  readonly origin: 'factory' | 'player';
  readonly contractId: string | null;
  readonly current: ModuleVersionDto;
  readonly versionCount: number;
  readonly installedIn: readonly { readonly frameId: string; readonly frameName: string; readonly slotIndex: number; readonly version: number }[];
  readonly updatedAt: string;
}

export interface ModuleDetailDto extends ModuleDto {
  readonly source: string;
  readonly versions: readonly ModuleVersionDto[];
}

export interface CreateModuleResponse {
  readonly module: ModuleDetailDto;
  readonly report: PipelineReportDto;
  readonly rewards: RewardGrantDto | null;
}

// ---------------------------------------------------------------------------------------
// Frames
// ---------------------------------------------------------------------------------------

export interface PartDto {
  readonly instanceId: string;
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly category: string;
  readonly tier: number;
  readonly stats: Readonly<Record<string, number>>;
  readonly grants: readonly string[];
}

export interface SoftwareSlotDto {
  readonly slotIndex: number;
  readonly module: {
    readonly id: string;
    readonly name: string;
    readonly category: ModuleCategory;
    readonly origin: 'factory' | 'player';
    readonly version: number;
    readonly latestVersion: number;
    readonly cycles: number;
    readonly grade: EfficiencyGrade;
  } | null;
}

export interface FrameStatsDto {
  readonly maxHealth: number;
  readonly maxEnergy: number;
  readonly energyRegen: number;
  readonly armor: number;
  readonly evasion: number;
  readonly heatDissipation: number;
  readonly cyclesPerTick: number;
  readonly memory: number;
  readonly threads: number;
}

/** Layered schematic art: `layers` are drawn in order (base first), all on one canvas. */
export interface FrameArtDto {
  readonly width: number;
  readonly height: number;
  /** Paths relative to the repo's `src/` art folder, e.g. `frames/X.png`, `modules/Y.png`. */
  readonly layers: readonly string[];
  readonly anchors: Partial<Record<SlotId, { readonly x: number; readonly y: number; readonly side: 'left' | 'right' }>>;
}

export interface FrameDto {
  readonly id: string;
  readonly definitionId: string;
  readonly name: string;
  readonly archetype: string;
  readonly tagline: string;
  readonly description: string;
  readonly level: number;
  readonly xp: number;
  readonly isActive: boolean;
  readonly stats: FrameStatsDto;
  readonly capabilities: readonly string[];
  readonly slots: readonly { readonly slot: SlotId; readonly accepts: readonly string[]; readonly part: PartDto | null }[];
  readonly software: readonly SoftwareSlotDto[];
  readonly cpuAllocated: number;
  /** Null when the Frame has no art yet (the client draws a line-art schematic). */
  readonly art: FrameArtDto | null;
}

export interface FrameCatalogEntryDto {
  readonly id: string;
  readonly name: string;
  readonly archetype: string;
  readonly tagline: string;
  readonly description: string;
  readonly strengths: readonly string[];
  readonly weaknesses: readonly string[];
  readonly owned: boolean;
  readonly acquisition: string;
}

/** A part the player owns, and where it is installed (null when it's spare). */
export interface OwnedPartDto extends PartDto {
  readonly installed: { readonly frameId: string; readonly slot: SlotId } | null;
}

/** Install a part into a hardware slot, or empty the slot with `partInstanceId: null`. */
export const InstallHardwareRequestSchema = z.object({
  slot: z.string().min(1).max(32),
  partInstanceId: z.string().uuid().nullable(),
});
export type InstallHardwareRequest = z.infer<typeof InstallHardwareRequestSchema>;

export const InstallSoftwareRequestSchema = z.object({
  moduleId: z.string().uuid(),
  slotIndex: z.number().int().min(0).max(15),
});
export type InstallSoftwareRequest = z.infer<typeof InstallSoftwareRequestSchema>;

// ---------------------------------------------------------------------------------------
// Arena
// ---------------------------------------------------------------------------------------

export interface ScenarioDto {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly kind: string;
  readonly enemies: readonly { readonly name: string; readonly kind: string; readonly maxHealth: number }[];
  readonly rewards: { readonly playerXp: number; readonly frameXp: number };
  readonly rewardsRemainingToday: number | null;
  readonly unlocked: boolean;
  readonly lockedReason?: string;
}

export const StartBattleRequestSchema = z.object({ scenarioId: z.string().max(100) });
export type StartBattleRequest = z.infer<typeof StartBattleRequestSchema>;

export interface BattleSummaryDto {
  readonly id: string;
  readonly scenarioId: string;
  readonly scenarioName: string;
  readonly outcome: CombatResult['outcome'];
  readonly ticks: number;
  readonly createdAt: string;
}

export interface BattleDto extends BattleSummaryDto {
  readonly result: CombatResult;
  readonly rewards: RewardGrantDto | null;
}
